import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { EwgInfos } from 'Apps/Main/Scenes/Ingredients/EwgInfos';
import { IngredientInfos } from 'Apps/Main/Scenes/Ingredients/IngredientInfos';

import { Typography } from 'Components/Typography';

import BlockLayout from 'Blocks/BlockLayout';

const Header = styled.div`
  ${legacyTheme.breakpoints.up('md')} {
    text-align: center;
  }
`;

const ListSection = styled.section`
  display: grid;
  direction: rtl;
  grid-template-columns: 100%;

  ${legacyTheme.breakpoints.up('sm')} {
    grid-template-columns: 10% 90%;
  }
`;

const IndexListWrapper = styled.div`
  display: none;
  white-space: nowrap;

  ${legacyTheme.breakpoints.up('sm')} {
    display: block;
  }
`;

const IndexList = styled.ul`
  position: sticky;
  top: 120px;

  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100%;
  margin: 0;
  padding: 0;

  list-style: none;
`;

const ListItem = styled.li`
  display: block;
  width: 100%;

  line-height: ${legacyTheme.spacing.s16};
  text-align: center;

  ${legacyTheme.breakpoints.up('sm')} {
    line-height: ${legacyTheme.spacing.s24};
  }

  & a {
    ${{ ...legacyTheme.typography.p2 }};
    display: block;
    width: 100%;
    color: inherit;
  }
`;

const IngredientsContainer = styled.div`
  direction: ltr;
  width: 100%;
  padding-top: 90px;
`;

const IngredientsWrapper = styled.div`
  display: flex;
  flex-flow: wrap;
  justify-content: space-between;
`;

const Ingredient = styled.div`
  width: 47%;
  margin-bottom: ${legacyTheme.spacing.s24};

  text-transform: capitalize;
  color: ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.match('sm')} {
    width: 100%;
  }

  & a {
    text-decoration: none;
  }
`;

const Ellipse = styled(Typography)`
  overflow: hidden;
  max-width: 100%;
  white-space: nowrap;

  text-overflow: ellipsis;

  & a {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const IngredientInformation = styled.div`
  display: flex;
  flex-flow: row wrap;
`;

type IngredientDetail = {
  alternate_name: string;
  benefits: string[];
  ewg_rating: number;
  locations: string[];
  name: string;
  present_in: {
    label: string;
    slug: string;
  }[];
  slug: string;
};

type CompleteListProps = {
  content: {
    title: string;
    description: string | ReactNode;
  };
  dataClickButton: string;
  dataClickLink: string;
  dataFrom: string;
  ingredients: [string, IngredientDetail[]?][];
};

export const CompleteList = ({
  content,
  dataFrom,
  dataClickButton,
  dataClickLink,
  ingredients: ingredientsList,
}: CompleteListProps) => {
  return (
    // @ts-expect-error - BlockLayout has no TS types yet
    <BlockLayout contentWidth="wide" direction="column" id="ingredients-full-list">
      <Header>
        <Typography paragraph variant="h1r">
          {content.title}
        </Typography>

        <Typography paragraph variant="p1">
          {content.description}
        </Typography>
        <EwgInfos />
      </Header>

      <ListSection>
        <IndexListWrapper>
          {/* Vertical index (alphabet) - each letter serves as a link to the first ingredient that begins with that letter */}
          <IndexList>
            {ingredientsList.map(([ingredientGroupLetter]) => {
              return (
                <ListItem key={ingredientGroupLetter}>
                  <a
                    aria-label={`Jump to ${ingredientGroupLetter} ingredients`}
                    data-click={dataClickButton}
                    data-from={dataFrom}
                    href={`#ingredients-${ingredientGroupLetter}`}
                  >
                    {ingredientGroupLetter}
                  </a>
                </ListItem>
              );
            })}
          </IndexList>
        </IndexListWrapper>

        <div>
          {ingredientsList.map(([ingredientGroupLetter, ingredients]) => {
            if (!ingredients) return null;
            return (
              <IngredientsContainer
                key={ingredientGroupLetter}
                data-custom={ingredientGroupLetter}
                id={`ingredients-${ingredientGroupLetter}`}
              >
                {/* Alphabet letter */}

                <Typography paragraph variant="h1">
                  {ingredientGroupLetter}
                </Typography>
                {/* All ingredients grouped for this letter */}
                <IngredientsWrapper>
                  {ingredients.map(
                    ({ alternate_name, benefits, ewg_rating, locations, name, slug }) => {
                      return (
                        <Ingredient key={slug}>
                          <Ellipse data-click={dataClickLink} data-from={dataFrom} variant="p2">
                            <Link to={`/ingredients/${slug}`}>{name}</Link>
                          </Ellipse>
                          <IngredientInformation>
                            <IngredientInfos>
                              ewg score: {ewg_rating > 0 ? ewg_rating : 'N/A'}
                            </IngredientInfos>
                            {alternate_name ? (
                              <IngredientInfos dash>{alternate_name}</IngredientInfos>
                            ) : null}
                            {locations?.length > 0 &&
                              locations.map((location, locationIndex) => (
                                <IngredientInfos key={location} dash={locationIndex === 0}>
                                  {location}
                                  {locationIndex < locations.length - 1 && <>,&nbsp;</>}
                                </IngredientInfos>
                              ))}
                            {benefits?.[0] && <IngredientInfos dash>{benefits[0]}</IngredientInfos>}
                          </IngredientInformation>
                        </Ingredient>
                      );
                    },
                  )}
                </IngredientsWrapper>
              </IngredientsContainer>
            );
          })}
        </div>
      </ListSection>
    </BlockLayout>
  );
};
